import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizationDetails } from 'Api/UcrmTprm/UcrmTprmApi';
import { Button, Link } from 'Components/Buttons/Buttons';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE, ICON_DELETE_REMOVE, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { getAddFolderVersionUrl, getManageFolderOriginBreadcrumbSegments, getManageFolderUrl, parseManageFolderOrigin } from 'Helpers/URLBuilder/URLBuilder';
import { FolderAllVersions, FolderBreadcrumbSource } from 'Models/Folders';
import { OrganizationResponseBase } from 'Models/Organization';
import { DueDiligenceModule } from 'Models/ProductModule';

import { DeleteFolderModal, DeleteFolderModalProps } from './DeleteFolderModal/DeleteFolderModal';
import { FolderVersionHistoryTable } from '../Components/FolderVersionHistoryTable';
import { FolderVersionSnapshot } from '../Components/FolderVersionSnapshot';

export interface FolderDetailsProps {
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

interface UrlParams {
    third_party_id: string;
    folder_id: string;
}

export const FolderDetails = (props: FolderDetailsProps) => {
    const { third_party_id, folder_id } = useParams<keyof UrlParams>() as UrlParams;
    const location = useLocation();
    const origin = parseManageFolderOrigin(new URLSearchParams(location.search), props.module);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [folderDeleted, setFolderDeleted] = useState<boolean>();
    const [showModal, setShowModal] = useState<boolean>();
    const [organization, setOrganization] = useState<OrganizationResponseBase>();
    const [versions, setVersions] = useState<FolderAllVersions>();

    useEffect(() => {
        const getAllVersions = async (): Promise<void> => {
            try {
                const response = await props.moduleApi.getAllFolderVersions(third_party_id, folder_id);
                setVersions(response.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getAllVersions();
    }, [props.moduleApi, third_party_id, folder_id]);

    useEffect(() => {
        const fetchOrganization = async (): Promise<void> => {
            try {
                const organizationResponse = await getOrganizationDetails(props.moduleApi, third_party_id);
                setOrganization(organizationResponse.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        fetchOrganization();
    }, [props.moduleApi, third_party_id]);

    const getModalProps = (): DeleteFolderModalProps => {
        return {
            moduleApi: props.moduleApi,
            folder: versions!.current_version,
            hideModal: () => setShowModal(false),
            organizationId: third_party_id,
            onFolderDeleted: () => setFolderDeleted(true),
        };
    };

    if (organization && versions) {
        return (
            <>
                {showModal && <DeleteFolderModal {...getModalProps()} />}
                <PageLayoutDefault
                    headerBreadcrumb={
                        <Breadcrumb textColor="blue">
                            {getManageFolderOriginBreadcrumbSegments(origin).map((segment, index) =>
                                segment.link ? (
                                    <BreadcrumbLink key={index} link={segment.link}>
                                        {segment.label}
                                    </BreadcrumbLink>
                                ) : (
                                    <BreadcrumbText key={index}>{segment.label}</BreadcrumbText>
                                )
                            )}
                            {/* When coming from a Folders-specific page, contextualize the folder by including the organization's name. When coming from an assessment subject's dashboard, exclude the organization's name, since the assessment subject's name is already present. Consider how this should be updated if/when we add an organization Profile/Details/Dashboard page. */}
                            {origin.source !== FolderBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD && <BreadcrumbText>{organization.name}</BreadcrumbText>}
                            <BreadcrumbText>{versions.current_version.name}</BreadcrumbText>
                        </Breadcrumb>
                    }
                    headerTitle={versions.current_version.name}
                    headerDescription={`Manage documents related to ${organization.name}, with an optional effective date and expiration date. When a new version of the folder is added, the current version of the folder will be recorded in Folder Version History.`}
                    headerButtons={
                        <>
                            <Button variant="danger" onClick={() => setShowModal(true)} fontAwesomeImage={ICON_DELETE_REMOVE} disabled={folderDeleted}>
                                Delete
                            </Button>
                            <Link variant="primaryButton" to={getAddFolderVersionUrl(third_party_id, folder_id, { source: FolderBreadcrumbSource.FOLDERS_LISTING, module: origin.module })} fontAwesomeImage={ICON_ADD_CREATE} disabled={folderDeleted}>
                                Add New Version
                            </Link>
                        </>
                    }
                    body={[
                        {
                            title: 'Current Folder Version',
                            buttons: (
                                <Link variant="secondaryButton" to={getManageFolderUrl(third_party_id, folder_id, { source: FolderBreadcrumbSource.FOLDERS_LISTING, module: origin.module })} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE} disabled={folderDeleted}>
                                    Manage
                                </Link>
                            ),
                            content: <FolderVersionSnapshot documentApi={props.documentApi} version={versions.current_version} />,
                        },
                        {
                            title: 'Folder Version History',
                            content: <FolderVersionHistoryTable documentApi={props.documentApi} versions={versions.past_versions} />,
                        },
                    ]}
                />
            </>
        );
    } else if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    } else {
        return <Placeholder />;
    }
};
